/**
 * TodoList
 *
 * A component for displaying interactive todo lists in chat messages
 *
 * Primary responsibilities:
 * - Display a list of todo items
 * - Allow marking items as complete/incomplete
 * - Track progress of todo list completion
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { IconList, IconCheck, IconArrowsShuffle } from '@tabler/icons-react'
import { motion, AnimatePresence } from 'framer-motion'

interface TodoListProps {
  items: string[]
  className?: string
}

type ParsedTodoItem = {
  completed: boolean
  text: string
}

const COMPLETED_MARKER_REGEX = /^\s*(?:\[x\]|\[done\]|✅|✓|✔)\s*/i
const PENDING_MARKER_REGEX = /^\s*(?:\[\s?\]|○)\s*/i

function parseTodoItem(item: string): ParsedTodoItem {
  if (COMPLETED_MARKER_REGEX.test(item)) {
    return {
      completed: true,
      text: item.replace(COMPLETED_MARKER_REGEX, '')
    }
  }

  return {
    completed: false,
    text: item.replace(PENDING_MARKER_REGEX, '')
  }
}

export const TodoList: React.FC<TodoListProps> = ({ items, className }) => {
  const parsedItems = useMemo(() => items.map(parseTodoItem), [items])
  const parsedItemsKey = parsedItems
    .map((item) => `${item.completed ? '1' : '0'}:${item.text}`)
    .join('\u0000')
  const initialCompletedItems = useMemo(() => {
    const indexes = new Set<number>()

    parsedItems.forEach((item, index) => {
      if (item.completed) indexes.add(index)
    })

    return indexes
  }, [parsedItems])
  const [completedItems, setCompletedItems] = useState<Set<number>>(
    () => new Set(initialCompletedItems)
  )
  const previousItemsKeyRef = useRef(parsedItemsKey)

  useEffect(() => {
    if (previousItemsKeyRef.current === parsedItemsKey) return

    previousItemsKeyRef.current = parsedItemsKey
    setCompletedItems(new Set(initialCompletedItems))
  }, [initialCompletedItems, parsedItemsKey])

  const toggleItem = (index: number) => {
    const newCompletedItems = new Set(completedItems)
    if (newCompletedItems.has(index)) {
      newCompletedItems.delete(index)
    } else {
      newCompletedItems.add(index)
    }
    setCompletedItems(newCompletedItems)
  }

  const resetAll = () => {
    setCompletedItems(new Set())
  }

  const completeAll = () => {
    const allItems = new Set<number>()
    parsedItems.forEach((_, index) => allItems.add(index))
    setCompletedItems(allItems)
  }

  const completedCount = completedItems.size
  const totalCount = parsedItems.length
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25,
        duration: 0.4
      }}
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <motion.div
        className="flex items-center border-b p-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <IconList size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">Todo List</h3>
          <motion.span
            className="text-muted-foreground text-xs"
            animate={{ scale: completedCount > 0 ? [1, 1.1, 1] : 1 }}
            transition={{ duration: 0.3 }}
          >
            ({completedCount}/{totalCount})
          </motion.span>
        </div>
      </motion.div>

      <div className="p-2">
        <AnimatePresence>
          {progress > 0 && (
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              exit={{ opacity: 0, scaleX: 0 }}
              transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
              className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
            >
              <motion.div
                className="h-full rounded-full bg-blue-500 transition-all"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ delay: 0.4, duration: 0.8, ease: 'easeOut' }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <ul className="space-y-2">
          {parsedItems.map((item, index) => (
            <motion.li
              key={index}
              className="hover:bg-accent/50 flex items-start gap-2 rounded-md p-2 transition-colors"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.1 + index * 0.05,
                duration: 0.3,
                ease: 'easeOut'
              }}
              whileHover={{
                scale: 1.02,
                backgroundColor: 'rgba(0,0,0,0.02)',
                transition: { duration: 0.15 }
              }}
            >
              <Checkbox
                id={`todo-item-${index}`}
                checked={completedItems.has(index)}
                onCheckedChange={() => toggleItem(index)}
                className="mt-0.5"
              />
              <motion.label
                htmlFor={`todo-item-${index}`}
                className={cn(
                  'flex-1 cursor-pointer text-sm leading-relaxed',
                  completedItems.has(index) && 'text-muted-foreground line-through'
                )}
                animate={{
                  opacity: completedItems.has(index) ? 0.6 : 1,
                  scale: completedItems.has(index) ? 0.98 : 1
                }}
                transition={{ duration: 0.2 }}
              >
                {item.text}
              </motion.label>
            </motion.li>
          ))}
        </ul>

        <motion.div
          className="mt-4 flex items-center gap-2 border-t pt-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button variant="outline" size="sm" onClick={resetAll} className="text-xs">
              <IconArrowsShuffle size={14} className="mr-1" />
              Reset
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button variant="outline" size="sm" onClick={completeAll} className="text-xs">
              <IconCheck size={14} className="mr-1" />
              Complete All
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  )
}
